import React, { useState, useEffect, useCallback } from 'react';
import { Button, Card, Typography, List, Alert, Spin, Collapse, message } from 'antd';
import MaterialIcon from '../../../Icons/MaterialIcon';
import { ProfessionalType } from '../../../../types';
import jsPDF from 'jspdf';

const { Title, Paragraph } = Typography;
const { Panel } = Collapse;

// Type definitions for the questionnaire response
interface QuestionResponse {
  id: number;
  questionId: string;
  answerText: string;
}

interface QuestionnaireResponse {
  id: number;
  questionnaireId: string;
  userId: string;
  clientId: string;
  sessionId: string;
  status: string;
  startedAt: string;
  languageCode: string;
  questionResponses: QuestionResponse[];
}

// Type definitions for questionnaire structure
interface QuestionText {
  en: string;
  nl: string;
}

interface Question {
  id: string;
  type: string;
  text: QuestionText;
  options: any[];
  columns: any[];
  required: boolean;
  sortOrder: number;
}

interface Section {
  id: string;
  title: QuestionText;
  questions: Question[];
}

interface QuestionnaireStructure {
  id: string;
  title: QuestionText;
  description: QuestionText;
  sections: Section[];
  isActive: boolean;
  version: number;
}

// AI Analysis types
interface PrimarySecondaryAnalysis {
  primary_ie: string;
  secondary_ie: string[];
  narrative: string;
}

interface BiochemicalMarkers {
  direct: string[];
  indirect: string[];
}

interface RecommendedQuestionnaire {
  nr: number;
  label: string;
  ID: string;
}

interface HetaRecommendation {
  nr: number;
  heta_id: string;
  heta_json_code: string;
}

interface AIAnalysisResponse {
  "client-name": string;
  "professional-name": string;
  A1: PrimarySecondaryAnalysis;
  A2: string[];
  A3: BiochemicalMarkers;
  B1: RecommendedQuestionnaire[];
  B2: RecommendedQuestionnaire[];
  B3: HetaRecommendation[];
}

interface NewAIQuestionnaireProfessionalProps {
  // The professional user, for language preference
  user?: ProfessionalType;
  // The client whose results are being viewed - using same pattern as Client component
  client: any;
  // Flag to enable/disable resubmit analysis feature
  enableResubmitAnalysis?: boolean;
}

const NewAIQuestionnaireProfessional: React.FC<NewAIQuestionnaireProfessionalProps> = ({ 
  user, 
  client, 
  enableResubmitAnalysis = true // Default to enabled
}) => {
  const [questionnaireResponses, setQuestionnaireResponses] = useState<QuestionnaireResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [questionnaireStructure, setQuestionnaireStructure] = useState<QuestionnaireStructure | null>(null);
  const [loadingStructure, setLoadingStructure] = useState(false);
  const [resubmittingAnalysis, setResubmittingAnalysis] = useState(false);

  // Log component render and props - matching Client component pattern
  console.log('[NewAIQuestionnaireProfessional] Component rendered with props:');
  console.log('- user:', user);
  console.log('- client:', client);
  console.log('- client.id:', client?.id);
  console.log('- client.email:', client?.email);
  console.log('- client.firstName:', client?.firstName);
  console.log('- client.familyName:', client?.familyName);
  console.log('- client.gid:', client?.gid);

  // Determine the language from the professional's profile, default to 'en'
  const _userAny = user as any;
  const detectedLanguage = _userAny?.userAttributes?.language ?? _userAny?.language ?? 'en';
  const lang = detectedLanguage === 'nl' ? 'nl' : 'en';
  
  console.log('[NewAIQuestionnaireProfessional] Language detection:');
  console.log('- detectedLanguage:', detectedLanguage);
  console.log('- final lang:', lang);

  const loadingText = {
    en: 'Loading questionnaire responses...',
    nl: 'Vragenlijst antwoorden laden...',
  };

  const noResultsText = {
    en: 'The client has not yet completed the My Health Intake questionnaire.',
    nl: 'De cliënt heeft de My Health Intake vragenlijst nog niet voltooid.',
  };

  // Function to fetch questionnaire structure
  const fetchQuestionnaireStructure = useCallback(async (questionnaireId: string) => {
    console.log('[NewAIQuestionnaireProfessional] Fetching questionnaire structure for:', questionnaireId);
    setLoadingStructure(true);

    try {
      const API_URL = process.env.REACT_APP_QUESTIONNAIRE_API;
      const API_KEY = process.env.REACT_APP_QUESTIONNAIRE_API_KEY;

      if (!API_KEY) {
        throw new Error('API_KEY is not configured');
      }

      const response = await fetch(`${API_URL}/api/v1/questionnaires/${questionnaireId}`, {
        method: "GET",
        mode: "cors",
        headers: {
          'Authorization': API_KEY,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('[NewAIQuestionnaireProfessional] Questionnaire structure fetched:', data);
      setQuestionnaireStructure(data);
    } catch (err: any) {
      console.error('[NewAIQuestionnaireProfessional] Failed to fetch questionnaire structure:', err);
    } finally {
      setLoadingStructure(false);
    }
  }, []);

  const fetchQuestionnaireResponses = useCallback(async () => {
    if (!client?.id) {
      console.error('[NewAIQuestionnaireProfessional] Client ID is missing:', client);
      setError('Client ID is required to fetch questionnaire responses');
      return;
    }

    console.log('[NewAIQuestionnaireProfessional] Starting API call for client:', client.id);
    console.log('[NewAIQuestionnaireProfessional] Full client data:', client);
    
    setLoading(true);
    setError(null);

    try {
      const API_URL = process.env.REACT_APP_QUESTIONNAIRE_API;
      const API_KEY = process.env.REACT_APP_QUESTIONNAIRE_API_KEY;


      // Use client ID for the API endpoint, matching Client component pattern
      const fullUrl = `${API_URL}/api/v1/questionnaire-responses/user/${client.id}`;
      console.log('[NewAIQuestionnaireProfessional] Full API URL:', fullUrl);

      console.log('[NewAIQuestionnaireProfessional] Making API call...');
      
      if (!API_KEY) {
        throw new Error('API_KEY is not configured');
      }

      const response = await fetch(`${API_URL}/api/v1/questionnaire-responses/user/${client.id}`, {
        method: "GET",
        mode: "cors",
        headers: {
          'Authorization': API_KEY,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      console.log('[NewAIQuestionnaireProfessional] API call successful!');
      console.log('- Status:', response.status);
      console.log('- Status Text:', response.statusText);
      console.log('- Response Data:', data);
      console.log('- Data type:', typeof data);
      console.log('- Data length (if array):', Array.isArray(data) ? data.length : 'Not an array');

      if (Array.isArray(data)) {
        data.forEach((item, index) => {
          console.log(`[NewAIQuestionnaireProfessional] Response item ${index}:`, item);
        });
      }

      setQuestionnaireResponses(data);
      if (data && data.length > 0) {
        setShowResults(true); // Auto-show results if available
        
        // Fetch questionnaire structure for the first response
        if (data[0].questionnaireId) {
          await fetchQuestionnaireStructure(data[0].questionnaireId);
        }
      }
      
      console.log('[NewAIQuestionnaireProfessional] State updated successfully');
    } catch (err: any) {
      console.error('[NewAIQuestionnaireProfessional] API call failed:');
      console.error('- Error object:', err);
      console.error('- Error message:', err.message);
      console.error('- Error response:', err.response);
      console.error('- Response status:', err.response?.status);
      console.error('- Response data:', err.response?.data);
      console.error('- Response headers:', err.response?.headers);
      console.error('- Request config:', err.config);
      console.error('- Request URL:', err.config?.url);
      
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch questionnaire responses';
      console.error('[NewAIQuestionnaireProfessional] Setting error message:', errorMessage);
      setError(errorMessage);
    } finally {
      console.log('[NewAIQuestionnaireProfessional] API call completed, setting loading to false');
      setLoading(false);
    }
  }, [client, fetchQuestionnaireStructure]);

  // Auto-fetch questionnaire responses when client data is available - matching Client component pattern
  useEffect(() => {
    if (client?.id) {
      console.log('[NewAIQuestionnaireProfessional] Client data available, auto-fetching questionnaire responses');
      fetchQuestionnaireResponses();
    }
  }, [client, fetchQuestionnaireResponses, fetchQuestionnaireStructure]);

  const handleViewResults = () => {
    console.log('[NewAIQuestionnaireProfessional] handleViewResults called - toggling visibility');
    console.log('- Current showResults state:', showResults);
    setShowResults(!showResults);
  };

  const formatQuestionId = (questionId: string): string => {
    // Convert question IDs like 'gen-q3' to more readable format
    return questionId.replace(/-/g, ' ').toUpperCase();
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  // Helper function to get question text from questionnaire structure
  const getQuestionText = (questionId: string, language: string = 'en'): string => {
    if (!questionnaireStructure) {
      return formatQuestionId(questionId); // Fallback to formatted ID
    }

    // Search through all sections and questions to find the matching question ID
    for (const section of questionnaireStructure.sections) {
      for (const question of section.questions) {
        if (question.id === questionId) {
          return question.text[language as keyof QuestionText] || question.text.en;
        }
      }
    }

    // If not found, return formatted question ID as fallback
    return formatQuestionId(questionId);
  };

  // Function to generate PDF from questionnaire responses
  const generateQuestionnaireDataPdf = useCallback(async (): Promise<Blob> => {
    console.log('[NewAIQuestionnaireProfessional] Generating questionnaire data PDF...');

    try {
      if (!questionnaireResponses || questionnaireResponses.length === 0) {
        throw new Error('No questionnaire responses available for PDF generation');
      }

      // Create PDF document
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.width;
      const margin = 20;
      let yPosition = margin;

      // Helper function to add text with word wrapping
      const addText = (text: string, x: number, y: number, maxWidth: number, fontSize: number = 12) => {
        pdf.setFontSize(fontSize);
        const lines = pdf.splitTextToSize(text, maxWidth);
        pdf.text(lines, x, y);
        return y + (lines.length * fontSize * 0.4);
      };

      // Helper function to check if new page is needed
      const checkNewPage = (requiredSpace: number = 40) => {
        if (yPosition > pdf.internal.pageSize.height - requiredSpace) {
          pdf.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Helper function to safely extract value from client data field
      const extractValue = (field: any): string => {
        if (!field) return '';
        if (typeof field === 'string') return field;
        if (field.value !== undefined) return field.value || '';
        return String(field);
      };

      // PDF Header
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      yPosition = addText('Comprehensive Client Health Analysis', margin, yPosition, pageWidth - 2 * margin, 20);
      yPosition += 15;

      // Basic Client Information
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      yPosition = addText('Client Information', margin, yPosition, pageWidth - 2 * margin, 16);
      yPosition += 5;

      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      yPosition = addText(`Name: ${client?.firstName || ''} ${client?.familyName || ''}`, margin, yPosition, pageWidth - 2 * margin);
      yPosition = addText(`Client ID: ${client?.id || ''}`, margin, yPosition, pageWidth - 2 * margin);
      yPosition = addText(`Email: ${client?.email || ''}`, margin, yPosition, pageWidth - 2 * margin);
      yPosition = addText(`Date Generated: ${new Date().toLocaleDateString()}`, margin, yPosition, pageWidth - 2 * margin);
      yPosition += 10;

      // Enhanced Client Data Section
      if (client) {
        console.log('[NewAIQuestionnaireProfessional] Full client data for PDF:', client);

        // Biographical Information
        checkNewPage();
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        yPosition = addText('Biographical Information', margin, yPosition, pageWidth - 2 * margin, 14);
        yPosition += 5;

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        
        // Extract bio data if available
        if (client.client_bios && client.client_bios.length > 0) {
          const bio = client.client_bios[0];
          yPosition = addText(`Date of Birth: ${bio.date_of_birth || 'Not provided'}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Sex: ${extractValue(bio.sex)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Gender Identity: ${extractValue(bio.gender_identity)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Ethnicity/Race: ${extractValue(bio.ethnicity_or_race)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Height: ${bio.length ? bio.length + ' cm' : 'Not provided'}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Weight: ${bio.weight ? bio.weight + ' kg' : 'Not provided'}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Waist Circumference: ${bio.waist_circumference ? bio.waist_circumference + ' cm' : 'Not provided'}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Vegetarian: ${bio.is_vegetarian ? 'Yes' : 'No'}`, margin, yPosition, pageWidth - 2 * margin);
        }
        yPosition += 10;

        // Contact Information
        checkNewPage();
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        yPosition = addText('Contact Information', margin, yPosition, pageWidth - 2 * margin, 14);
        yPosition += 5;

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        
        if (client.client_personal_contact_details && client.client_personal_contact_details.length > 0) {
          const contact = client.client_personal_contact_details[0];
          yPosition = addText(`Street: ${extractValue(contact.address_street)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Number: ${extractValue(contact.address_number)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Place: ${extractValue(contact.address_place)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Postal Code: ${extractValue(contact.address_postal_code)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Country: ${extractValue(contact.address_country)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Residential Area: ${extractValue(contact.address_residential_area)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Phone: ${contact.telephone_number || 'Not provided'}`, margin, yPosition, pageWidth - 2 * margin);
        }
        yPosition += 10;

        // Family Information
        checkNewPage();
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        yPosition = addText('Family Information', margin, yPosition, pageWidth - 2 * margin, 14);
        yPosition += 5;

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        
        if (client.client_family_details && client.client_family_details.length > 0) {
          const family = client.client_family_details[0];
          yPosition = addText(`Marital Status: ${extractValue(family.marital_status)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Living Situation: ${extractValue(family.living_situation)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Mother Name: ${extractValue(family.mother_name)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Father Name: ${extractValue(family.father_name)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Parents Marital Status: ${extractValue(family.parents_marital_status)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Number of Brothers: ${family.number_of_brothers || 0}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Number of Sisters: ${family.number_of_sisters || 0}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Male Children: ${family.number_of_male_children || 0}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Female Children: ${family.number_of_female_children || 0}`, margin, yPosition, pageWidth - 2 * margin);
        }
        yPosition += 10;

        // Career and Education
        checkNewPage();
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        yPosition = addText('Career & Education', margin, yPosition, pageWidth - 2 * margin, 14);
        yPosition += 5;

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        
        if (client.client_career_and_education_details && client.client_career_and_education_details.length > 0) {
          const career = client.client_career_and_education_details[0];
          yPosition = addText(`Profession: ${extractValue(career.profession)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Occupational Status: ${extractValue(career.occupational_status)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Education Level: ${extractValue(career.education_level)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Salary Level: ${extractValue(career.salary)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Health Insurance: ${extractValue(career.health_insurance_status)}`, margin, yPosition, pageWidth - 2 * margin);
        }
        yPosition += 10;

        // Medical History - Allergies
        checkNewPage();
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        yPosition = addText('Medical History - Allergies', margin, yPosition, pageWidth - 2 * margin, 14);
        yPosition += 5;

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        
        if (client.client_allergies && client.client_allergies.length > 0) {
          client.client_allergies.forEach((allergy: any, index: number) => {
            yPosition = addText(`Allergy ${index + 1}: Option ID ${allergy.option_allergy_id}`, margin, yPosition, pageWidth - 2 * margin);
          });
        } else {
          yPosition = addText('No allergies recorded', margin, yPosition, pageWidth - 2 * margin);
        }
        yPosition += 10;

        // Medical History - Surgeries
        checkNewPage();
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        yPosition = addText('Medical History - Surgeries', margin, yPosition, pageWidth - 2 * margin, 14);
        yPosition += 5;

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        
        if (client.client_surgeries && client.client_surgeries.length > 0) {
          client.client_surgeries.forEach((surgery: any, index: number) => {
            checkNewPage(30);
            yPosition = addText(`Surgery ${index + 1}:`, margin, yPosition, pageWidth - 2 * margin);
            yPosition = addText(`  For What: ${extractValue(surgery.for_what)}`, margin, yPosition, pageWidth - 2 * margin);
            yPosition = addText(`  When: ${extractValue(surgery.when)}`, margin, yPosition, pageWidth - 2 * margin);
            yPosition += 5;
          });
        } else {
          yPosition = addText('No surgeries recorded', margin, yPosition, pageWidth - 2 * margin);
        }
        yPosition += 10;

        // Medications and Supplements
        checkNewPage();
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        yPosition = addText('Medications & Supplements', margin, yPosition, pageWidth - 2 * margin, 14);
        yPosition += 5;

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        
        if (client.client_food_supplements && client.client_food_supplements.length > 0) {
          yPosition = addText('Food Supplements:', margin, yPosition, pageWidth - 2 * margin);
          client.client_food_supplements.forEach((supplement: any, index: number) => {
            yPosition = addText(`  ${index + 1}. ${supplement.content || 'No details'}`, margin, yPosition, pageWidth - 2 * margin);
          });
        }

        if (client.client_other_medications && client.client_other_medications.length > 0) {
          yPosition = addText('Other Medications:', margin, yPosition, pageWidth - 2 * margin);
          client.client_other_medications.forEach((medication: any, index: number) => {
            yPosition = addText(`  ${index + 1}. ${medication.content || 'No details'}`, margin, yPosition, pageWidth - 2 * margin);
          });
        }
        yPosition += 10;

        // Lifestyle - Vice Frequencies
        checkNewPage();
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        yPosition = addText('Lifestyle Information', margin, yPosition, pageWidth - 2 * margin, 14);
        yPosition += 5;

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        
        if (client.client_vice_frequencies && client.client_vice_frequencies.length > 0) {
          client.client_vice_frequencies.forEach((vice: any) => {
            yPosition = addText(`${vice.type}: ${extractValue(vice.frequency)}`, margin, yPosition, pageWidth - 2 * margin);
          });
        }

        if (client.client_addictions && client.client_addictions.length > 0) {
          yPosition = addText('Addictions:', margin, yPosition, pageWidth - 2 * margin);
          client.client_addictions.forEach((addiction: any, index: number) => {
            yPosition = addText(`  Addiction ${index + 1}: Option ID ${addiction.option_addiction_id}`, margin, yPosition, pageWidth - 2 * margin);
          });
        }
        yPosition += 10;

        // Food Intolerances
        checkNewPage();
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        yPosition = addText('Food Intolerances', margin, yPosition, pageWidth - 2 * margin, 14);
        yPosition += 5;

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        
        if (client.client_food_intolerances && client.client_food_intolerances.length > 0) {
          client.client_food_intolerances.forEach((intolerance: any, index: number) => {
            yPosition = addText(`Intolerance ${index + 1}: Option ID ${intolerance.option_food_intolerance_id}`, margin, yPosition, pageWidth - 2 * margin);
          });
        } else {
          yPosition = addText('No food intolerances recorded', margin, yPosition, pageWidth - 2 * margin);
        }
        yPosition += 10;

        // Important Notes
        checkNewPage();
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        yPosition = addText('Important Notes for Professionals', margin, yPosition, pageWidth - 2 * margin, 14);
        yPosition += 5;

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        
        if (client.client_important_note_to_professionals && client.client_important_note_to_professionals.length > 0) {
          client.client_important_note_to_professionals.forEach((note: any, index: number) => {
            checkNewPage(40);
            yPosition = addText(`Note ${index + 1} (Year: ${note.year_of_event}):`, margin, yPosition, pageWidth - 2 * margin);
            yPosition = addText(`  ${note.note}`, margin, yPosition, pageWidth - 2 * margin);
            yPosition += 5;
          });
        } else {
          yPosition = addText('No important notes recorded', margin, yPosition, pageWidth - 2 * margin);
        }
        yPosition += 15;
      }

      // Questionnaire Responses Section
      checkNewPage();
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      yPosition = addText('Questionnaire Responses', margin, yPosition, pageWidth - 2 * margin, 16);
      yPosition += 10;

      questionnaireResponses.forEach((questionnaire, index) => {
        // Check if we need a new page
        checkNewPage(50);

        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        yPosition = addText(`Questionnaire ${index + 1}: ${questionnaire.questionnaireId}`, margin, yPosition, pageWidth - 2 * margin, 14);
        yPosition += 5;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'italic');
        yPosition = addText(`Status: ${questionnaire.status} | Started: ${new Date(questionnaire.startedAt).toLocaleDateString()}`, margin, yPosition, pageWidth - 2 * margin, 10);
        yPosition += 8;

        // Questions and Answers
        if (questionnaire.questionResponses && questionnaire.questionResponses.length > 0) {
          questionnaire.questionResponses.forEach((qr) => {
            // Check if we need a new page
            checkNewPage(60);

            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'bold');
            const questionText = getQuestionText(qr.questionId, questionnaire.languageCode);
            yPosition = addText(`Q: ${questionText}`, margin, yPosition, pageWidth - 2 * margin, 11);
            yPosition += 2;

            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            yPosition = addText(`A: ${qr.answerText}`, margin, yPosition, pageWidth - 2 * margin, 10);
            yPosition += 8;
          });
        }

        yPosition += 5;
      });

      return new Blob([pdf.output('blob')], { type: 'application/pdf' });

    } catch (error) {
      console.error('[NewAIQuestionnaireProfessional] Error generating PDF:', error);
      throw error;
    }
  }, [client, questionnaireResponses, getQuestionText]);

  // Function to resubmit questionnaire data for AI analysis
  const handleResubmitAnalysis = useCallback(async () => {
    if (!client?.id) {
      message.error(lang === 'en' ? 'Client ID is required for analysis resubmission' : 'Client-ID vereist voor heranalyse');
      return;
    }

    if (!questionnaireResponses || questionnaireResponses.length === 0) {
      message.error(lang === 'en' ? 'No questionnaire responses available for analysis' : 'Geen vragenlijst antwoorden beschikbaar voor analyse');
      return;
    }

    console.log('[NewAIQuestionnaireProfessional] Starting AI analysis resubmission for client:', client.id);
    setResubmittingAnalysis(true);

    try {
      const API_URL = process.env.REACT_APP_QUESTIONNAIRE_API;
      const API_KEY = process.env.REACT_APP_QUESTIONNAIRE_API_KEY;

      if (!API_KEY) {
        throw new Error('API_KEY is not configured');
      }

      // Generate PDF from questionnaire responses
      const questionnairePdf = await generateQuestionnaireDataPdf();
      
      // Prepare the form data - backend expects 'file' as the key for PDF
      const formData = new FormData();
      formData.append('clientId', client.id);
      formData.append('professionalName', user?.firstName || 'Professional');
      formData.append('clientName', `${client?.firstName || ''} ${client?.familyName || ''}`.trim());
      
      // Add additional client metadata for better AI analysis
      if (client.email) {
        formData.append('clientEmail', client.email);
      }
      
      // Add client bio data if available
      if (client.client_bios && client.client_bios.length > 0) {
        const bio = client.client_bios[0];
        if (bio.date_of_birth) {
          formData.append('clientDateOfBirth', bio.date_of_birth);
        }
        if (bio.sex?.value || bio.sex) {
          formData.append('clientSex', typeof bio.sex === 'string' ? bio.sex : bio.sex.value);
        }
        if (bio.weight) {
          formData.append('clientWeight', bio.weight.toString());
        }
        if (bio.length) {
          formData.append('clientHeight', bio.length.toString());
        }
      }
      
      // Add questionnaire metadata
      if (questionnaireResponses && questionnaireResponses.length > 0) {
        formData.append('questionnaireCount', questionnaireResponses.length.toString());
        formData.append('primaryQuestionnaireId', questionnaireResponses[0].questionnaireId);
        formData.append('questionnaireLanguage', questionnaireResponses[0].languageCode || 'en');
      }
      
      // Create a proper File object with the correct filename
      const pdfFileName = `${client.id}_comprehensive_health_analysis.pdf`;
      const pdfFile = new File([questionnairePdf], pdfFileName, { type: 'application/pdf' });
      formData.append('file', pdfFile);

      console.log('[NewAIQuestionnaireProfessional] Comprehensive form data prepared:');
      console.log('- clientId:', client.id);
      console.log('- professionalName:', user?.firstName || 'Professional');
      console.log('- clientName:', `${client?.firstName || ''} ${client?.familyName || ''}`.trim());
      console.log('- clientEmail:', client.email);
      console.log('- file name:', pdfFileName);
      console.log('- file size:', pdfFile.size);
      console.log('- questionnaire count:', questionnaireResponses?.length || 0);

      console.log('[NewAIQuestionnaireProfessional] Sending resubmit request to AI tool...');
      
      // Send to AI analysis endpoint
      const response = await fetch(`${API_URL}/api/v1/ai-tool-daisy/analyze`, {
        method: "POST",
        mode: "cors",
        headers: {
          'Authorization': API_KEY,
        },
        body: formData,
      });

      console.log('[NewAIQuestionnaireProfessional] Response received:');
      console.log('- status:', response.status);
      console.log('- statusText:', response.statusText);
      console.log('- ok:', response.ok);

      if (!response.ok) {
        // Try to get error details from response
        let errorDetails = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorBody = await response.text();
          console.log('[NewAIQuestionnaireProfessional] Error response body:', errorBody);
          errorDetails += ` - ${errorBody}`;
        } catch (e) {
          console.log('[NewAIQuestionnaireProfessional] Could not read error response body');
        }
        throw new Error(errorDetails);
      }

      const aiAnalysisData: AIAnalysisResponse = await response.json();
      console.log('[NewAIQuestionnaireProfessional] AI analysis response received:', aiAnalysisData);
      
      message.success(lang === 'en' ? 'Analysis submitted successfully!' : 'Analyse succesvol ingediend!');
      
    } catch (err: any) {
      console.error('[NewAIQuestionnaireProfessional] Failed to resubmit analysis:', err);
      const errorMessage = err.message || 'Failed to resubmit analysis';
      message.error(lang === 'en' ? errorMessage : 'Fout bij opnieuw indienen analyse');
    } finally {
      setResubmittingAnalysis(false);
    }
  }, [client, user, lang, questionnaireResponses, generateQuestionnaireDataPdf]);

  const renderQuestionnaireResponse = (response: QuestionnaireResponse) => {
    // Create a map of questionId to answerText for easy lookup
    const answerMap = new Map(response.questionResponses.map(qr => [qr.questionId, qr.answerText]));

    return (
      <Panel
        header={
          <div>
            <strong>{questionnaireStructure?.title[response.languageCode as keyof QuestionText] || questionnaireStructure?.title.en || response.questionnaireId}</strong>
            <div style={{ fontSize: '12px', color: '#666' }}>
              Status: {response.status} | Started: {formatDate(response.startedAt)} | Language: {response.languageCode} | Questions Answered: {response.questionResponses?.length || 0}
            </div>
          </div>
        }
        key={response.id}
      >
        {questionnaireStructure ? (
          <div>
            {questionnaireStructure.sections.map(section => (
              <div key={section.id} style={{ marginBottom: '24px' }}>
                <h3 style={{ marginTop: '16px', marginBottom: '12px', borderBottom: '1px solid #eee', paddingBottom: '8px', fontSize: '16px', fontWeight: 'bold' }}>
                  {section.title[response.languageCode as keyof QuestionText] || section.title.en}
                </h3>
                <List
                  dataSource={section.questions.sort((a, b) => a.sortOrder - b.sortOrder)}
                  renderItem={(question) => {
                    const answer = answerMap.get(question.id);
                    return (
                      <List.Item>
                        <div style={{ width: '100%' }}>
                          <strong style={{ display: 'block', marginBottom: '4px' }}>
                            {question.text[response.languageCode as keyof QuestionText] || question.text.en}
                          </strong>
                          {answer !== undefined ? (
                            <div style={{ color: '#333', backgroundColor: '#f5f5f5', padding: '8px 12px', borderRadius: '4px' }}>
                              {answer}
                            </div>
                          ) : (
                            <div style={{ color: '#999', fontStyle: 'italic' }}>
                              No answer provided.
                            </div>
                          )}
                        </div>
                      </List.Item>
                    );
                  }}
                />
              </div>
            ))}
          </div>
        ) : (
          // Fallback to original implementation if structure is not yet loaded
          <List
            dataSource={response.questionResponses}
            renderItem={(questionResponse) => (
              <List.Item>
                <div style={{ width: '100%' }}>
                  <strong>{getQuestionText(questionResponse.questionId, response.languageCode)}:</strong>
                  <div style={{ marginTop: '4px', color: '#333', backgroundColor: '#f5f5f5', padding: '8px', borderRadius: '4px' }}>
                    {questionResponse.answerText}
                  </div>
                </div>
              </List.Item>
            )}
          />
        )}
      </Panel>
    );
  };

  const hasResults = questionnaireResponses.length > 0;

  return (
    <Card>
      <Title level={5}>My Health Intake</Title>
      
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          style={{ marginBottom: 16 }}
        />
      )}

      {(loading || loadingStructure) ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 8 }}>
            {loading && loadingStructure 
              ? (lang === 'en' ? 'Loading responses and questionnaire details...' : 'Antwoorden en vragenlijst details laden...')
              : loading 
                ? loadingText[lang]
                : (lang === 'en' ? 'Loading questionnaire details...' : 'Vragenlijst details laden...')
            }
          </div>
        </div>
      ) : hasResults ? (
        <>
          <Paragraph>
            {lang === 'en' 
              ? `Found ${questionnaireResponses.length} questionnaire response(s) for this client.`
              : `${questionnaireResponses.length} vragenlijst antwoord(en) gevonden voor deze cliënt.`
            }
          </Paragraph>
          
          {/* Resubmit Analysis Section */}
          {enableResubmitAnalysis && (
            <div style={{ marginBottom: 16, padding: '12px', backgroundColor: '#f0f2ff', borderRadius: '6px', border: '1px solid #d6e4ff' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                <div>
                  <strong style={{ color: '#1890ff' }}>
                    {lang === 'en' ? 'Analysis' : 'Analyse'}
                  </strong>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                    {lang === 'en' 
                      ? 'Submit questionnaire data for analysis' 
                      : 'Vragenlijst data indienen voor analyse'
                    }
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button 
                    type="primary"
                    onClick={handleResubmitAnalysis}
                    loading={resubmittingAnalysis}
                    disabled={!questionnaireResponses || questionnaireResponses.length === 0}
                    icon={<MaterialIcon className="material-icons-sharp" icon="psychology" />}
                    size="small"
                  >
                    {resubmittingAnalysis 
                      ? (lang === 'en' ? 'Submitting...' : 'Indienen...')
                      : (lang === 'en' ? 'Submit for Analysis' : 'Indienen voor Analyse')
                    }
                  </Button>
                </div>
              </div>
            </div>
          )}

          <Button 
            type={showResults ? "default" : "primary"}
            onClick={handleViewResults}
            style={{ marginBottom: showResults ? 16 : 0 }}
            icon={<MaterialIcon className="material-icons-sharp" icon={showResults ? "visibility_off" : "visibility"} />}
          >
            {showResults 
              ? (lang === 'en' ? 'Hide Results' : 'Verberg Resultaten')
              : (lang === 'en' ? 'Show Results' : 'Toon Resultaten')
            }
          </Button>

          {showResults && (
            <Collapse defaultActiveKey={questionnaireResponses.map(r => r.id)}>
              {questionnaireResponses.map(renderQuestionnaireResponse)}
            </Collapse>
          )}
        </>
      ) : (
        <Paragraph>
          {noResultsText[lang]}
        </Paragraph>
      )}
    </Card>
  );
};

export default NewAIQuestionnaireProfessional;